import { ReactNode } from 'react';
import {
  ButtonGroup,
  GhostButton,
  IconButton,
  TertiaryButton,
  DragButton,
} from '@ardoq/button';
import { RuleRow } from './RuleRow';
import { Locale } from '@ardoq/locale';
import { toQuery, toRule } from '../stateUtils/utils';
import {
  AsyncLabelLoaders,
  AsyncSuggestionsLoaders,
  GetRuleNotificationParams,
  InternalQuery,
  InternalRow,
  InternalRule,
  SearchContext,
} from '../types';
import { QueryBuilderFilterDefinitionGroup } from '../filterUtils';
import {
  ConditionTag,
  DropPlaceholderBar,
  InvisibleDragOverBar,
  QueryBuilderRuleAndConditionWrapper,
  SubqueryHeaderWrapper,
  SubqueryWrapper,
} from './atoms';
import { AddIcon, ChevronRightIcon, IconName } from '@ardoq/icons';
import { SelectConditionBar } from './SelectConditionBar';
import { BooleanOperator } from '@ardoq/api-types';
import { first, last } from 'lodash';
import { useDndContext } from '../dndUtils/dndContext';
import {
  getDragImageOffset,
  isTargetDragHandleButton,
} from '@ardoq/drag-and-drop';
import { ReducerActions } from '../stateUtils/types';
import { ArdoqLoaderComponent, LoaderColor, LoaderSize } from '@ardoq/loader';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

type RuleOrQueryRowProps = {
  isFirst?: boolean;
  isLast?: boolean;
  condition: BooleanOperator;
  row: InternalRow;
  reducerActions: ReducerActions;
  filterGroups: QueryBuilderFilterDefinitionGroup[];
  level: number;
  maxSubqueryNestingLevel: number;
  asyncSuggestionsLoaders: AsyncSuggestionsLoaders;
  asyncLabelLoaders: AsyncLabelLoaders;
  showErrors?: boolean;
  isReadOnly: boolean;
  isDisabled: boolean;
  clickIdPrefix?: string;
  getRuleNotificationParams?: GetRuleNotificationParams;
  isCompact?: boolean;
  locale: Locale;
  i18nLocale?: I18nLocale;
  menuPortalTarget?: HTMLElement | null;
};

const RuleOrQueryRow = ({
  isFirst,
  isLast,
  condition,
  row,
  reducerActions,
  filterGroups,
  level,
  asyncSuggestionsLoaders,
  asyncLabelLoaders,
  maxSubqueryNestingLevel,
  showErrors,
  isReadOnly,
  isDisabled,
  clickIdPrefix,
  getRuleNotificationParams,
  isCompact,
  locale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  menuPortalTarget,
}: RuleOrQueryRowProps) => {
  const {
    setDraggedRow,
    setDragOverRule,
    getRenderPlaceholderPosition,
    getIsDragglable: getIsDraggable,
  } = useDndContext();
  const renderPlaceholderPosition = getRenderPlaceholderPosition(row.uuid);
  const rule = toRule<InternalRule>(row);
  const query = toQuery<InternalQuery>(row);
  return (
    <>
      {renderPlaceholderPosition === 'above' && (
        <DropPlaceholderBar
          $isSubqueryContext={level > 1}
          $isAbove
          $isFirst={isFirst}
        />
      )}
      <QueryBuilderRuleAndConditionWrapper
        $isFirst={isFirst}
        $isLast={isLast}
        $isCompact={isCompact}
        $isDisabled={isDisabled}
      >
        {!(isCompact && isFirst) && (
          <ConditionTag
            condition={condition}
            isCompact={isCompact}
            isDisabled={isDisabled}
            i18nLocale={i18nLocale}
          />
        )}

        {rule && (
          <div
            onDragOver={e => {
              const rect = (e.target as HTMLElement).getBoundingClientRect();
              const middleOfRect = rect.top + rect.height / 2;
              const position = middleOfRect > e.clientY ? 'above' : 'below';
              setDragOverRule({
                overRuleId: rule.uuid,
                position,
              });
            }}
            onDragStart={event => {
              event.stopPropagation(); // without this we cannot drag rows inside nested filters
              if (!isTargetDragHandleButton(event.target)) {
                return;
              }
              // allow dragging only by button, but display entire rule when dragged
              const { offsetX, offsetY } = getDragImageOffset(
                event.currentTarget.getBoundingClientRect(),
                event.clientX,
                event.clientY
              );
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setDragImage(
                event.currentTarget,
                offsetX,
                offsetY
              );
              setDraggedRow({
                draggedRowId: rule.uuid,
                draggedRowType: 'rule',
              });
            }}
            style={{ flex: 1 }}
          >
            <RuleRow
              isDraggable={
                !isDisabled && !isReadOnly && getIsDraggable(rule.uuid)
              }
              rule={rule}
              filterGroups={filterGroups}
              reducerActions={reducerActions}
              asyncSuggestionsLoaders={asyncSuggestionsLoaders}
              asyncLabelLoaders={asyncLabelLoaders}
              showErrors={showErrors}
              isReadOnly={isReadOnly}
              isDisabled={isDisabled}
              clickIdPrefix={clickIdPrefix}
              getRuleNotificationParams={getRuleNotificationParams}
              isCompact={isCompact}
              locale={locale}
              i18nLocale={i18nLocale}
              menuPortalTarget={menuPortalTarget}
            />
          </div>
        )}
        {query && (
          <QueryRow
            level={level}
            query={query}
            filterGroups={filterGroups}
            reducerActions={reducerActions}
            asyncSuggestionsLoaders={asyncSuggestionsLoaders}
            asyncLabelLoaders={asyncLabelLoaders}
            maxSubqueryNestingLevel={maxSubqueryNestingLevel}
            showErrors={showErrors}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            getRuleNotificationParams={getRuleNotificationParams}
            isCompact={isCompact}
            locale={locale}
            i18nLocale={i18nLocale}
            menuPortalTarget={menuPortalTarget}
          />
        )}
      </QueryBuilderRuleAndConditionWrapper>
      {renderPlaceholderPosition === 'below' && (
        <DropPlaceholderBar $isSubqueryContext={level > 1} />
      )}
    </>
  );
};

type QueryRowProps = {
  query: InternalQuery;
  filterGroups: QueryBuilderFilterDefinitionGroup[];
  reducerActions: ReducerActions;
  level: number;
  asyncSuggestionsLoaders: AsyncSuggestionsLoaders;
  asyncLabelLoaders: AsyncLabelLoaders;
  maxSubqueryNestingLevel: number;
  includeContextSelect?: boolean;
  contextSelectIsReadOnly?: boolean;
  searchContext?: SearchContext;
  showErrors?: boolean;
  allowedSearchContexts?: SearchContext[];
  isReadOnly: boolean;
  isDisabled: boolean;
  hideTopLevelConditionBar?: boolean;
  clickIdPrefix?: string;
  topLevelLabelContent?: ReactNode;
  getRuleNotificationParams?: GetRuleNotificationParams;
  isCompact?: boolean;
  isLoading?: boolean;
  locale: Locale;
  i18nLocale?: I18nLocale;
  menuPortalTarget?: HTMLElement | null;
};

export const QueryRow = ({
  query,
  reducerActions,
  filterGroups,
  level,
  asyncSuggestionsLoaders,
  asyncLabelLoaders,
  includeContextSelect,
  contextSelectIsReadOnly,
  searchContext,
  maxSubqueryNestingLevel,
  showErrors,
  allowedSearchContexts,
  isReadOnly,
  isDisabled,
  hideTopLevelConditionBar,
  clickIdPrefix,
  topLevelLabelContent,
  getRuleNotificationParams,
  isCompact,
  isLoading,
  locale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  menuPortalTarget,
}: QueryRowProps) => {
  const {
    setDraggedRow,
    resetState,
    getIsDragglable: getIsDraggable,
    setDragOverRule,
  } = useDndContext();
  const isDraggable =
    !isDisabled && !isReadOnly && level > 0 && getIsDraggable(query.uuid);

  return (
    <SubqueryWrapper
      $level={level}
      $isCompact={isCompact}
      $isReadOnly={isReadOnly}
      onDragStart={event => {
        event.stopPropagation();
        if (!isTargetDragHandleButton(event.target)) {
          return;
        }
        // allow dragging only by button, but display entire rule when dragged
        const { offsetX, offsetY } = getDragImageOffset(
          event.currentTarget.getBoundingClientRect(),
          event.clientX,
          event.clientY
        );
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setDragImage(event.currentTarget, offsetX, offsetY);
        setDraggedRow({
          draggedRowId: query.uuid,
          draggedRowType: 'query',
        });
      }}
      onDragEnd={() => {
        resetState();
      }}
    >
      <InvisibleDragOverBar
        onDragEnterCapture={() => {
          setDragOverRule({ overRuleId: query.uuid, position: 'above' });
        }}
      />
      <SubqueryHeaderWrapper>
        {!isReadOnly && level > 0 && (
          <DragButton draggable={isDraggable} i18nLocale={i18nLocale} />
        )}
        {isLoading && level === 0 && (
          <ArdoqLoaderComponent
            size={LoaderSize.SMALL}
            loaderColor={LoaderColor.LIGHT}
            loaderType="spinner"
            loaderText=""
          />
        )}
        {hideTopLevelConditionBar && level === 0 ? null : (
          <SelectConditionBar
            searchContext={searchContext}
            onSearchContextChange={reducerActions.updateSearchContext}
            includeContextSelect={includeContextSelect}
            condition={query.condition}
            allowedSearchContexts={allowedSearchContexts}
            contextSelectIsReadOnly={contextSelectIsReadOnly}
            onConditionChange={condition => {
              reducerActions.updateRow(query.uuid, { condition });
            }}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            clickIdPrefix={clickIdPrefix}
            topLevelLabelContent={level === 0 ? topLevelLabelContent : null}
            i18nLocale={i18nLocale}
            menuPortalTarget={menuPortalTarget}
          />
        )}
        {!isReadOnly && level > 0 && (
          <IconButton
            label={t(i18nLocale, 'queryBuilder:button.removeRow', 'Remove row')}
            iconName={IconName.CLOSE}
            isDisabled={isDisabled}
            onClick={() => reducerActions.removeRow({ uuid: query.uuid })}
            dataTestId="remove-row-button"
          />
        )}
      </SubqueryHeaderWrapper>
      {query.rules.map(row => (
        <RuleOrQueryRow
          key={row.uuid}
          isFirst={first(query.rules) === row}
          isLast={last(query.rules) === row}
          condition={query.condition}
          level={level + 1}
          maxSubqueryNestingLevel={maxSubqueryNestingLevel}
          row={row}
          filterGroups={filterGroups}
          reducerActions={reducerActions}
          asyncSuggestionsLoaders={asyncSuggestionsLoaders}
          asyncLabelLoaders={asyncLabelLoaders}
          showErrors={showErrors}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
          clickIdPrefix={clickIdPrefix}
          getRuleNotificationParams={getRuleNotificationParams}
          isCompact={isCompact}
          locale={locale}
          i18nLocale={i18nLocale}
          menuPortalTarget={menuPortalTarget}
        />
      ))}
      {!isReadOnly && (
        <ButtonGroup>
          <TertiaryButton
            isDisabled={isDisabled}
            onClick={() => {
              reducerActions.addRow({
                parentUuid: query.uuid,
                rowType: 'rule',
              });
            }}
            dataTestId="add-rule"
            data-click-id={clickIdPrefix ? `${clickIdPrefix}add-rule` : null}
          >
            <AddIcon />
            {t(i18nLocale, 'queryBuilder:button.addRule', 'Add rule')}
          </TertiaryButton>
          {level < maxSubqueryNestingLevel && (
            <GhostButton
              isDisabled={isDisabled}
              onClick={() => {
                reducerActions.addRow({
                  parentUuid: query.uuid,
                  rowType: 'query',
                });
              }}
              dataTestId="add-subquery"
              data-click-id={
                clickIdPrefix ? `${clickIdPrefix}add-subquery` : null
              }
            >
              <ChevronRightIcon />
              {t(i18nLocale, 'queryBuilder:button.addSubquery', 'Add subquery')}
            </GhostButton>
          )}
        </ButtonGroup>
      )}
      <InvisibleDragOverBar
        onDragEnterCapture={() => {
          setDragOverRule({ overRuleId: query.uuid, position: 'below' });
        }}
      />
    </SubqueryWrapper>
  );
};
